import { useEffect, useState, type FormEvent } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Eye,
  EyeOff,
  KeyRound,
  LockKeyhole,
  Mail,
} from "lucide-react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { usePortal } from "../state/PortalContext";
import { Button, TextInput } from "./ui";

type AuthMode = "sign-in" | "register" | "forgot" | "confirmation" | "update";
type ConfirmationKind = "registration" | "reset";

function modeForPath(pathname: string): Exclude<AuthMode, "confirmation"> {
  if (pathname.endsWith("/register")) return "register";
  if (pathname.endsWith("/forgot-password")) return "forgot";
  if (pathname.endsWith("/reset-password")) return "forgot";
  return "sign-in";
}

function friendlyAuthError(error: unknown) {
  const fallback = "Something went wrong. Please try again.";
  if (!(error instanceof Error)) return fallback;

  const message = error.message.toLowerCase();
  if (message.includes("invalid login credentials")) {
    return "The email or password is incorrect.";
  }
  if (message.includes("email not confirmed")) {
    return "Confirm your email before signing in.";
  }
  if (
    message.includes("already registered") ||
    message.includes("user already exists")
  ) {
    return "An account already exists for this email. Sign in instead.";
  }
  if (message.includes("password") && message.includes("weak")) {
    return "Choose a stronger password with at least 8 characters.";
  }
  if (message.includes("rate limit")) {
    return "Too many attempts. Please wait a moment and try again.";
  }
  return error.message || fallback;
}

export function AuthScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    session,
    authError,
    passwordRecovery,
    registerWithPassword,
    signInWithPassword,
    requestPasswordReset,
    completePasswordRecovery,
  } = usePortal();
  const recoveryActive = passwordRecovery && Boolean(session);
  const [mode, setMode] = useState<AuthMode>(
    recoveryActive ? "update" : modeForPath(location.pathname),
  );
  const [confirmationKind, setConfirmationKind] =
    useState<ConfirmationKind>("registration");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    setMode(recoveryActive ? "update" : modeForPath(location.pathname));
  }, [location.pathname, recoveryActive]);

  useEffect(() => {
    document.title = "Log in to access your products";
  }, []);

  useEffect(() => {
    if (session && !recoveryActive) {
      void navigate({ to: "/account", replace: true });
    }
  }, [navigate, recoveryActive, session]);

  const changeMode = (nextMode: AuthMode) => {
    setMode(nextMode);
    setMessage("");
    setPassword("");
    setConfirmPassword("");
    setShowPassword(false);

    const route =
      nextMode === "register"
        ? "/register"
        : nextMode === "forgot"
          ? "/forgot-password"
          : nextMode === "update"
            ? "/reset-password"
            : "/login";
    void navigate({ to: route, replace: nextMode === "update" });
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setMessage("");

    if (
      (mode === "register" || mode === "update") &&
      password !== confirmPassword
    ) {
      setMessage("The passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      if (mode === "sign-in") {
        await signInWithPassword(email, password);
      } else if (mode === "register") {
        const result = await registerWithPassword(email, password);
        if (result.needsEmailConfirmation) {
          setConfirmationKind("registration");
          setMode("confirmation");
        }
      } else if (mode === "forgot") {
        await requestPasswordReset(email);
        setConfirmationKind("reset");
        setMode("confirmation");
      } else if (mode === "update") {
        await completePasswordRecovery(password);
        void navigate({ to: "/account", replace: true });
      }
    } catch (error) {
      setMessage(friendlyAuthError(error));
    } finally {
      setSubmitting(false);
    }
  };

  const passwordField = (
    label: string,
    value: string,
    onChange: (value: string) => void,
    autoComplete: "current-password" | "new-password",
  ) => (
    <label className="field">
      <span className="field__label">{label}</span>
      <span className="auth-input auth-input--password">
        <LockKeyhole size={17} aria-hidden="true" />
        <TextInput
          type={showPassword ? "text" : "password"}
          autoComplete={autoComplete}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          minLength={8}
          required
        />
        <button
          className="auth-password-toggle"
          type="button"
          aria-label={showPassword ? "Hide password" : "Show password"}
          onClick={() => setShowPassword((visible) => !visible)}
        >
          {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
        </button>
      </span>
    </label>
  );

  const normalizedEmail = email.trim().toLowerCase();
  const canSubmit =
    mode === "forgot"
      ? Boolean(normalizedEmail)
      : mode === "update"
        ? password.length >= 8 && confirmPassword.length >= 8
        : Boolean(normalizedEmail) &&
          password.length >= 8 &&
          (mode !== "register" || confirmPassword.length >= 8);

  const isConfirmation = mode === "confirmation";
  const isUpdate = mode === "update";

  return (
    <main className="auth-page">
      <section className="auth-card" aria-labelledby="auth-title">
        {!isConfirmation && !isUpdate && mode !== "forgot" ? (
          <div className="auth-mode-tabs" role="tablist" aria-label="Account access">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "sign-in"}
              className={mode === "sign-in" ? "is-active" : ""}
              onClick={() => changeMode("sign-in")}
            >
              Log in
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "register"}
              className={mode === "register" ? "is-active" : ""}
              onClick={() => changeMode("register")}
            >
              Create account
            </button>
          </div>
        ) : null}

        <div className="auth-card__eyebrow">
          {isUpdate ? <KeyRound size={15} /> : <LockKeyhole size={15} />}
          {isUpdate
            ? "Password recovery"
            : isConfirmation
              ? "Email confirmation"
              : mode === "forgot"
                ? "Account recovery"
                : "Secure account"}
        </div>

        <h1 id="auth-title">
          {mode === "register"
            ? "Create your account"
            : mode === "forgot"
              ? "Reset your password"
              : mode === "confirmation"
                ? confirmationKind === "registration"
                  ? "Confirm your email"
                  : "Check your email"
                : mode === "update"
                  ? "Choose a new password"
                  : "Log in to access your products"}
        </h1>

        <p>
          {mode === "register"
              ? "Use your email address and a password you will remember."
            : mode === "forgot"
              ? "We’ll send one password reset link to your email."
              : mode === "confirmation"
                ? confirmationKind === "registration"
                  ? "We sent one confirmation email. Open it to activate your account."
                  : "We sent a password reset link to your email."
                : mode === "update"
                  ? "Enter the password you want to use for future sign-ins."
                  : "Enter the email and password connected to your purchase."}
        </p>

        {isConfirmation ? (
          <>
            <div className="auth-sent" role="status">
              <span><Check size={18} /></span>
              <div>
                <strong>
                  {confirmationKind === "registration"
                    ? "One confirmation email sent"
                    : "Password reset email sent"}
                </strong>
                <small>
                  {confirmationKind === "registration"
                    ? "After confirming, you can log in with your password."
                    : "Open the link to choose a new password."}
                </small>
              </div>
            </div>
            <button
              className="auth-reset"
              type="button"
              onClick={() => changeMode("sign-in")}
            >
              <ArrowLeft size={14} />
              Back to log in
            </button>
          </>
        ) : (
          <form onSubmit={submit} className="auth-form">
            {mode !== "update" ? (
              <label className="field">
                <span className="field__label">Email address</span>
                <span className="auth-input">
                  <Mail size={17} aria-hidden="true" />
                  <TextInput
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@example.com"
                    required
                  />
                </span>
              </label>
            ) : null}

            {mode === "sign-in"
              ? passwordField(
                  "Password",
                  password,
                  setPassword,
                  "current-password",
                )
              : null}

            {mode === "register" || mode === "update" ? (
              <>
                {passwordField(
                  mode === "update" ? "New password" : "Password",
                  password,
                  setPassword,
                  "new-password",
                )}
                {passwordField(
                  "Confirm password",
                  confirmPassword,
                  setConfirmPassword,
                  "new-password",
                )}
                <small className="auth-password-hint">Use at least 8 characters.</small>
              </>
            ) : null}

            <Button type="submit" disabled={submitting || !canSubmit}>
              {submitting
                ? mode === "sign-in"
                  ? "Signing in…"
                  : mode === "register"
                    ? "Creating account…"
                    : mode === "forgot"
                      ? "Sending reset link…"
                      : "Saving password…"
                : mode === "sign-in"
                  ? "Log in"
                  : mode === "register"
                    ? "Create account"
                    : mode === "forgot"
                      ? "Send reset link"
                      : "Save new password"}
              {!submitting ? <ArrowRight size={16} /> : null}
            </Button>
          </form>
        )}

        {mode === "sign-in" ? (
          <button
            className="auth-reset"
            type="button"
            onClick={() => changeMode("forgot")}
          >
            Forgot password?
          </button>
        ) : mode === "forgot" ? (
          <button
            className="auth-reset"
            type="button"
            onClick={() => changeMode("sign-in")}
          >
            <ArrowLeft size={14} />
            Back to log in
          </button>
        ) : null}

        {message || authError ? (
          <p className="auth-error" role="alert">{message || authError}</p>
        ) : null}
      </section>
    </main>
  );
}
